import React, { useState, useEffect } from 'react';
import { Bell, X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { notificationService } from '../services/notificationService';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';

const NotificationOnboarding = () => {
  const { theme } = useTheme();
  const { t } = useLocale();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  
  // Detect device type
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
  const isPWA = isStandalone;

  useEffect(() => {
    // Check if we should show the prompt
    const checkNotificationStatus = async () => {
      // Check if user clicked "Remind me later" - respect their choice
      const laterUntil = localStorage.getItem('notification_prompt_later_until');
      if (laterUntil) {
        const laterTime = parseInt(laterUntil, 10);
        if (Date.now() < laterTime) {
          console.log('🔔 NotificationOnboarding: Skipping - user said remind later (until ' + new Date(laterTime).toLocaleDateString() + ')');
          return;
        } else {
          // Time has passed, clear the flag
          localStorage.removeItem('notification_prompt_later_until');
        }
      }
      
      // Check if user permanently dismissed
      const dismissed = localStorage.getItem('notification_prompt_dismissed');
      if (dismissed === 'true') {
        console.log('🔔 NotificationOnboarding: Skipping - user dismissed permanently');
        return;
      }
      
      // Check if there's an actual push subscription (not just settings enabled)
      try {
        const statusResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/push/status`,
          { credentials: 'include' }
        );
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          console.log('🔔 Push status:', statusData);
          
          // If user has a valid subscription, don't show popup
          if (statusData.subscribed && !statusData.needs_resubscription) {
            console.log('🔔 NotificationOnboarding: Skipping - valid push subscription exists');
            localStorage.setItem('notifications_enabled', 'true');
            return;
          }
          
          // If needs resubscription, show the popup even if localStorage says enabled
          if (statusData.needs_resubscription) {
            console.log('🔔 NotificationOnboarding: Showing - needs resubscription');
            // Don't return, continue to show popup
          }
        }
      } catch (error) {
        console.error('Error checking push status:', error);
      }
      
      // On iOS, if not installed as PWA, show install guide instead
      if (isIOS && !isPWA) {
        setTimeout(() => {
          setShowInstallGuide(true);
          setShow(true);
        }, 2000);
        return;
      }
      
      // Don't show if notifications not supported
      if (!('Notification' in window)) {
        console.log('Notifications not supported on this device');
        return;
      }
      
      const permission = Notification.permission;
      
      // If already denied, don't show
      if (permission === 'denied') return;
      
      // If already granted but no subscription, still show popup to create subscription
      if (permission === 'granted') {
        console.log('🔔 NotificationOnboarding: Browser permission granted but no subscription - showing popup to subscribe');
        // Continue to show popup so user can create subscription
      }
      
      // Show prompt after a short delay
      setTimeout(() => setShow(true), 2000);
    };
    
    checkNotificationStatus();
  }, [isIOS, isPWA]);

  const handleEnable = async () => {
    setLoading(true);
    console.log('🔔 NotificationOnboarding: Enable button clicked');
    console.log('  - isIOS:', isIOS);
    console.log('  - isAndroid:', isAndroid);
    console.log('  - isPWA:', isPWA);
    
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        alert(isIOS 
          ? 'To enable notifications on iPhone:\n\n1. Tap the Share button (square with arrow)\n2. Tap "Add to Home Screen"\n3. Open the app from your Home Screen\n4. Enable notifications again'
          : 'Notifications are not supported on this device');
        setLoading(false);
        return;
      }
      
      console.log('🔔 Current permission:', Notification.permission);
      
      // Request permission directly (must be user-initiated)
      let permission;
      try {
        permission = await Notification.requestPermission();
        console.log('🔔 Permission result:', permission);
      } catch (permError) {
        console.error('❌ Error requesting permission:', permError);
        alert('Could not request notification permission. Please check your device settings.');
        setLoading(false);
        return;
      }
      
      if (permission !== 'granted') {
        console.log('⚠️ Permission not granted:', permission);
        if (permission === 'denied') {
          alert(isIOS
            ? 'Notifications blocked. Go to Settings → LifeFlo → Notifications → Allow'
            : 'Notifications blocked. Please enable in your browser/app settings.');
        }
        setLoading(false);
        return;
      }
      
      console.log('✅ Permission granted! Subscribing to push...');
      
      // Subscribe to push notifications
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      let pushSubscriptionSuccess = false;
      let pushError = null;
      
      try {
        await notificationService.subscribeToPush(apiUrl);
        console.log('✅ Push subscription successful');
        pushSubscriptionSuccess = true;
      } catch (subError) {
        console.error('❌ Push subscription failed:', subError);
        pushError = subError.message;
        
        // For iOS, provide specific guidance
        if (isIOS) {
          alert(`⚠️ Notification setup failed.\n\nPlease try:\n1. Close and reopen the app\n2. Go to Settings → Notifications\n3. Click Enable there\n\nError: ${subError.message}`);
        } else {
          alert(`⚠️ Push subscription failed: ${subError.message}\n\nTry again from Settings.`);
        }
      }
      
      // CRITICAL: Only sync settings as "enabled" if push subscription succeeded
      // Otherwise the backend will think notifications are working when they're not
      try {
        const settingsResponse = await fetch(`${apiUrl}/api/notifications/settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            enabled: pushSubscriptionSuccess, // Only true if subscription worked
            water_reminders: true,
            task_reminders: true,
            bill_reminders: true,
            medicine_reminders: true,
            morning_greeting: true,
            evening_summary: true
          })
        });
        
        if (!settingsResponse.ok) {
          console.error('❌ Settings sync failed with status:', settingsResponse.status);
        } else {
          console.log('✅ Settings synced with enabled:', pushSubscriptionSuccess);
        }
      } catch (syncError) {
        console.error('❌ Settings sync error:', syncError);
      }
      
      // Show feedback notification
      try {
        if (pushSubscriptionSuccess) {
          new Notification('🔔 Notifications Enabled!', {
            body: 'You will now receive reminders from LifeFlo',
            icon: '/logo192.png'
          });
        }
      } catch (testError) {
        console.warn('⚠️ Test notification failed:', testError);
      }
      
      // CRITICAL: Only mark as enabled if push subscription actually worked
      if (pushSubscriptionSuccess) {
        localStorage.setItem('notifications_enabled', 'true');
        localStorage.setItem('notification_prompt_dismissed', 'true');
      } else {
        // Don't dismiss prompt - let user try again later
        localStorage.setItem('notifications_enabled', 'false');
      }
      
      setShow(false);
      
    } catch (error) {
      console.error('❌ Error enabling notifications:', error);
      alert('Failed to enable notifications: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    // User clicked X - don't show again for this session
    localStorage.setItem('notification_prompt_dismissed', 'true');
    setShow(false);
  };

  const handleLater = () => {
    // User clicked "Remind me later" - don't bother them for 7 days
    const laterUntil = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7 days from now
    localStorage.setItem('notification_prompt_later_until', String(laterUntil));
    setShow(false);
  };

  if (!show) return null;

  // iOS Install Guide (when not installed as PWA)
  if (showInstallGuide && isIOS && !isPWA) {
    return (
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleLater();
        }}
      >
        <div 
          className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl animate-in slide-in-from-bottom-4 duration-300 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            type="button"
          >
            <X className="h-5 w-5" />
          </button>

          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: '#007AFF15' }}
          >
            <Download className="h-8 w-8 text-blue-500" />
          </div>

          <h3 className="text-xl font-semibold text-gray-800 text-center mb-2">
            Install LifeFlo App
          </h3>
          <p className="text-gray-600 text-center text-sm mb-6">
            To receive notifications on iPhone, add LifeFlo to your Home Screen first.
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-sm">
            <p className="font-medium text-gray-700 mb-3">How to install:</p>
            <ol className="space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                <span>Tap the <strong>Share</strong> button <span className="text-blue-500">⬆️</span> at the bottom</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                <span>Scroll down and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
                <span>Tap <strong>&ldquo;Add&rdquo;</strong> in the top right</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
                <span>Open LifeFlo from your Home Screen</span>
              </li>
            </ol>
          </div>

          <div className="space-y-2">
            <button
              type="button"
              onClick={handleLater}
              className="w-full h-10 text-gray-500 hover:text-gray-700 text-sm"
            >
              I&apos;ll do this later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Standard notification prompt
  return (
    <div 
      className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleLater();
      }}
    >
      <div 
        className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl animate-in slide-in-from-bottom-4 duration-300 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          type="button"
        >
          <X className="h-5 w-5" />
        </button>

        <div 
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: `${theme?.colors?.primary}15` }}
        >
          <Bell className="h-8 w-8" style={{ color: theme?.colors?.primary }} />
        </div>

        <h3 className="text-xl font-semibold text-gray-800 text-center mb-2">
          {t('notifications.stayOnTrack')}
        </h3>
        <p className="text-gray-600 text-center text-sm mb-6">
          {t('notifications.remindYouAbout')}
        </p>

        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <p className="text-xs text-gray-500 mb-2">{t('notifications.gentleReminders')}</p>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">💧 {t('home.water')}</span>
            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">💊 {t('health.medicines')}</span>
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">📄 {t('life.bills')}</span>
            <span 
              className="text-xs px-2 py-1 rounded-full"
              style={{ backgroundColor: `${theme?.colors?.primary}15`, color: theme?.colors?.primary }}
            >
              ✅ {t('home.tasks')}
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            type="button"
            onClick={handleEnable}
            disabled={loading}
            className="w-full h-12 text-white rounded-xl font-medium"
            style={{ backgroundColor: theme?.colors?.primary }}
          >
            {loading ? t('notifications.enabling') : t('notifications.enableNotifications')}
          </Button>
          <button
            type="button"
            onClick={handleLater}
            className="w-full h-10 text-gray-500 hover:text-gray-700 text-sm"
          >
            {t('notifications.maybeLater')}
          </button>
        </div>

        <p className="text-xs text-gray-400 text-center mt-4">
          {t('notifications.changeAnytime')} {theme?.emojis?.heart || '💜'}
        </p>
      </div>
    </div>
  );
};

export default NotificationOnboarding;
